'use client';

import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';

import useIsMobile from '@hyex/utils/hooks/useIsMobile';

import ProductOrderTypes from '../options/delivery';
import ProductVariants from '../options/variants';
import type { Product } from '@hyex/dxp/types/store/Product/Product';
import ProductDescription from '@hyex/modules/shared/Product/common/ProductDescription';
import { convertPrice, isDefault } from '@hyex/modules/portal/domain/domain.utils';
import AddCartButton from '@hyex/modules/shared/base/AddCartButton';
import { findBestBottlePrice } from '../../../../utils/products/findBestBottlePrice';
import { parseZeroSymbolToNumber } from '../../../../utils/products/parseZeroSymbolToNumber';
import { OrderType } from '../../../../../../dxp/src/types/domain/OrderType';
import ProductPrice from '@hyex/modules/shared/Product/common/ProductPrice';
import { ProductVariantType } from '@hyex/dxp/types/domain/ProductVariantType';
import Image from 'next/image';
import productImg from './images/stemx-6pack.png';

import Modal, { useModalDialog } from '@hyex/ui/Modal';

export type PropTypes = {
  product: Product;
  onNewSelectedSku?: (sku: ProductVariantType) => void;
  onNewSelectedOrderType: (x: OrderType) => void;
  onNewQuantity: (quantity: number) => void;
  setSelectedIndex: (index: number) => void;
  productVariants: ProductVariantType[];
  productAutoshipVariants: ProductVariantType[];
  selectedVariantIndex: number;

  orderTypes: OrderType[];

  defaultOrderType: OrderType;

  defaultOneTimePrice: string;
  defaultAutoShipPrice: string;

  onAddCart: (variant: ProductVariantType) => void;
  addCartDisabled: boolean;
};

const ProductOrderForm: FC<PropTypes> = ({
  onNewSelectedSku,
  onNewSelectedOrderType,
  onNewQuantity,
  setSelectedIndex,

  product,
  productVariants,
  productAutoshipVariants,
  orderTypes,
  defaultOrderType,

  selectedVariantIndex,

  defaultOneTimePrice,
  defaultAutoShipPrice,

  onAddCart,
  addCartDisabled,
}) => {
  const isMobile = useIsMobile(501);

  const [quantity, setQuantity] = useState(1);

  const [orderType, setOrderType] = useState(defaultOrderType);

  useEffect(() => {
    onNewSelectedOrderType(orderType);

    if (orderType.autoship === true) {
      setQuantity(1);
    }
  }, [onNewSelectedOrderType, orderType]);

  useEffect(() => {
    onNewQuantity(quantity);
  }, [onNewQuantity, quantity]);

  const getProductPriceMemo = useMemo(() => {
    const variant = productVariants[selectedVariantIndex];

    const isOnSale = variant?.isOnSale ?? false;

    const isOldPriceDefault = isDefault(variant?.oldPrice);

    const isCurrentPriceDefault = isDefault(variant?.price);

    if (isOnSale && !isCurrentPriceDefault && !isOldPriceDefault) {
      return variant?.oldPrice ?? '';
    }

    if (!isOnSale && !isCurrentPriceDefault) {
      return variant.price;
    }

    return product?.price ?? 'N/A';
  }, [productVariants, selectedVariantIndex]);

  const getProductVariant = useCallback(() => {
    const variant = productVariants[selectedVariantIndex];
    return variant;
  }, [productVariants, selectedVariantIndex]);

  const getOneBottleProductVariant = useCallback(() => {
    const variant = productVariants.find((v) => v.skuId.indexOf('1') === 0);
    return variant;
  }, [productVariants]);

  const isProductVariantsValid = () =>
    productVariants && productVariants.length > selectedVariantIndex;

  const showBottlePriceRule = () => {
    const isVariantsExist = isProductVariantsValid();

    if (!isVariantsExist) return false;

    const skuId = productVariants[selectedVariantIndex].skuId;

    return orderType.autoship && parseZeroSymbolToNumber(skuId) >= 3;
  };

  const averageBottleVariantPrice = () => {
    const isVariantsExist = isProductVariantsValid();

    if (!isVariantsExist) return 0;

    const variant = productVariants[selectedVariantIndex];

    return convertPrice(variant.price) / parseZeroSymbolToNumber(variant.skuId);
  };

  const getAutoShipVariant = (sku: string) => {
    const autoShipVariant = productAutoshipVariants.find((variant) => variant.skuId.includes(sku));
    return autoShipVariant;
  };

  const modalDialog = useModalDialog();

  const [isModalActive, setModalActive] = useState(false);

  const toggleModal = () => {
    if (!isModalActive) {
      setModalActive(true);
    } else {
      setModalActive(false);
    }

    modalDialog.open();
  };

  const toggleModalOrAddToCart = () => {
    if (hasAutoshipSku()) {
      toggleModal();
    } else {
      onAddCart(getProductVariant());
    }
  };

  const hasAutoshipSku = () => {
    const productVariant = getProductVariant();

    const autoShipVariant = getAutoShipVariant(productVariant.skuId);

    return !!autoShipVariant;
  };

  const getNumberOfBottles = (sku: string) => {
    const skuNumber = sku.match(/\d+/g) ?? [''];
    const skuNumberString = skuNumber.join('');

    return skuNumberString;
  };

  const getAutoShipPopupText = (autoShipVariant: ProductVariantType) => {
    const oneBottleProductVariant = getOneBottleProductVariant();
    const numberOfBottlesStr = getNumberOfBottles(autoShipVariant.skuId);

    const autoshipPrice = convertPrice(autoShipVariant.price);
    const oneBottleOneTimePrice = convertPrice(oneBottleProductVariant?.price || '');

    const numberOfBottles = Number(numberOfBottlesStr);

    const offEveryOrder = oneBottleOneTimePrice * numberOfBottles - autoshipPrice;

    const bottlePrice = autoshipPrice / numberOfBottles;

    let reshipsEvery = 30 * numberOfBottles;

    return (
      <ul className="bullets">
        <li>
          Get <b>${offEveryOrder.toFixed(2)} off</b>{' '}
          {numberOfBottles === 3 ? 'every 3 bottle order' : 'every order'}
        </li>
        {numberOfBottles !== 1 && (
          <li>
            <b>FREE shipping</b> included
          </li>
        )}
        <li>
          <b>Each bottle for ${bottlePrice.toFixed(2)}</b> (discount applied)
        </li>
        <li>Automatically reships every {reshipsEvery} days</li>
      </ul>
    );
  };

  const handleAddToCart = (variant: ProductVariantType) => {
    toggleModal();
    onAddCart(variant);
  };

  const modal = () => {
    const productVariant = getProductVariant();
    const autoShipVariant = getAutoShipVariant(productVariant.skuId);
    const numberOfBottlesStr = getNumberOfBottles(productVariant.skuId);
    const numberOfBottles = Number(numberOfBottlesStr);

    return (
      <Modal
        {...modalDialog}
        onClose={() => {
          setModalActive(false);
        }}
        classes={{ body: 'body-modal-subscription' }}
      >
        {autoShipVariant && (
          <div className="product-image">
            <Image src={autoShipVariant?.image} alt="product-img" width="150" height="200" />
          </div>
        )}

        <div className="product-title">
          <h2>Subscribe and SAVE {numberOfBottles !== 1 ? 'ANOTHER' : ''} 10%</h2>

          {autoShipVariant && getAutoShipPopupText(autoShipVariant)}
        </div>
        <div className="product-actions">
          <button
            className="yes-btn"
            onClick={() => autoShipVariant && handleAddToCart(autoShipVariant)}
            disabled={addCartDisabled || product?.isOutOfStock === true}
          >
            Yes! <span>Lock in my instant Savings NOW!</span>
          </button>

          <button
            className="no-btn"
            onClick={() => handleAddToCart(getProductVariant())}
            disabled={addCartDisabled || product?.isOutOfStock === true}
          >
            No! <span>Just send me my order</span>
          </button>
        </div>
      </Modal>
    );
  };

  return (
    <div className="main-product-block">
      <>
        <div className="product-info-block">
          <h1 className="product-name">{product.title}</h1>
          <ProductDescription
            description={product.description}
            shortDescription={product?.shortDescription ?? ''}
          />
          <div className="sku">
            SKU: {productVariants[selectedVariantIndex]?.skuId ?? product.sku}
          </div>
        </div>
      </>
      <>
        <ProductVariants
          options={productVariants}
          orderTypes={orderTypes}
          setSelectedSku={(x: any) => {}}
          defaultSelectedIndex={selectedVariantIndex}
          ignoreThisIndex={productVariants.length}
          setSelectedIndex={setSelectedIndex}
        />
      </>
      <div className="product-actions">
        <AddCartButton
          text={
            addCartDisabled
              ? 'Already In Cart'
              : product?.isOutOfStock === true
              ? 'Sold Out'
              : 'Add To Cart'
          }
          className="cart-btn colored"
          onClick={toggleModalOrAddToCart}
          disabled={addCartDisabled || product?.isOutOfStock === true}
        />
      </div>

      {isModalActive && modal()}
    </div>
  );
};

export default ProductOrderForm;
